/**
 * Module-level in-flight request coalescing for read-only workflow-hub
 * endpoints (`/configurations`, `/views`, `/assigned-users`). When two
 * callers ask for the same URL within the same tick — the canonical case
 * being the VMS tab's `useVmsConfig` hook firing in parallel with the inner
 * `WorkflowProvider`'s mount-time refreshes, AND React 18 Strict Mode in
 * development double-mounting components — they share one network
 * round-trip instead of each running its own request and discarding the
 * loser's payload.
 *
 * Scope on purpose: only idempotent READ endpoints. Writes and search
 * (with mutable filter/pagination params) have different cache-invalidation
 * concerns and stay outside this helper.
 *
 * Lifecycle: each entry is dropped the moment its promise settles. We
 * coalesce *concurrent* calls, not subsequent ones — so explicit refresh
 * actions (refreshConfiguration(force=true), cache invalidation after a
 * write, a fresh view-save round-trip) still hit the network rather than
 * seeing a stale resolved value.
 */

const inFlight = new Map<string, Promise<unknown>>()

export async function coalesceWorkflowFetch<T>(
  url: string,
  fetcher: () => Promise<T>,
): Promise<T> {
  const existing = inFlight.get(url) as Promise<T> | undefined
  if (existing) {
    return existing
  }

  const promise = (async () => {
    try {
      return await fetcher()
    } finally {
      inFlight.delete(url)
    }
  })()

  inFlight.set(url, promise)
  return promise
}
